import { zodResolver } from '@hookform/resolvers/zod';
import {
  eggDetailSchema,
  eggInputSchema,
  eggVariableInputSchema,
  okSchema,
  type EggDetail,
  type EggInput,
  type EggVariable,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  Cpu,
  Download,
  Loader2,
  Pencil,
  Plus,
  Save,
  Trash2,
  Egg as EggIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { Confirm } from '@/components/confirm';
import { FormField } from '@/components/form-field';
import { LazyCodeEditor } from '@/components/lazy-code-editor';
import { PageHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { apiRequest } from '@/lib/api';
import { allEggsQuery, gameProfilesQuery, nestsQuery, nodesQuery } from '@/lib/admin-queries';
import { applyServerErrors, errorMessage } from '@/lib/forms';
import { timeAgo } from '@/lib/format';

export const Route = createFileRoute('/_app/admin/eggs/$eggId')({ component: EggPage });

const eggKey = (id: number) => ['admin', 'eggs', id] as const;

function EggPage() {
  const eggId = Number(Route.useParams().eggId);
  const query = useQuery({
    queryKey: eggKey(eggId),
    queryFn: ({ signal }) =>
      apiRequest(`/api/application/eggs/${eggId}`, { schema: eggDetailSchema, signal }),
  });
  return (
    <>
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/admin/nests">
          <ArrowLeft /> Nests & eggs
        </Link>
      </Button>
      <QueryState query={query}>{(egg) => <EggEditor key={egg.id} egg={egg} />}</QueryState>
    </>
  );
}

/** Image map as "Label | image" lines, which is easy to edit in a textarea. */
const imagesToText = (m: Record<string, string>) =>
  Object.entries(m)
    .map(([k, v]) => (k === v ? v : `${k} | ${v}`))
    .join('\n');
const textToImages = (t: string) =>
  Object.fromEntries(
    t
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const [label, image] = l.includes('|') ? l.split('|').map((s) => s.trim()) : [l, l];
        return [label!, image ?? label!];
      }),
  );
const listToText = (l: string[]) => l.join('\n');
const textToList = (t: string) =>
  t
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);

function EggEditor({ egg }: { egg: EggDetail }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState('configuration');
  const form = useForm<EggInput>({
    resolver: zodResolver(eggInputSchema),
    defaultValues: {
      nestId: egg.nestId,
      name: egg.name,
      description: egg.description ?? '',
      author: egg.author,
      features: egg.features,
      dockerImages: egg.dockerImages,
      fileDenylist: egg.fileDenylist,
      updateUrl: egg.updateUrl,
      startup: egg.startup,
      configFiles: egg.configFiles,
      configStartup: egg.configStartup,
      configLogs: egg.configLogs,
      configStop: egg.configStop,
      configFromId: egg.configFromId,
      scriptInstall: egg.scriptInstall,
      scriptContainer: egg.scriptContainer,
      scriptEntry: egg.scriptEntry,
      copyScriptFromId: egg.copyScriptFromId,
      profileSlug: egg.profileSlug,
    },
  });
  const save = useMutation({
    mutationFn: (body: EggInput) =>
      apiRequest(`/api/application/eggs/${egg.id}`, {
        method: 'PATCH',
        body,
        schema: eggDetailSchema,
      }),
    onSuccess: async (updated, values) => {
      queryClient.setQueryData(eggKey(egg.id), updated);
      form.reset(values);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'eggs'], exact: true });
      toast.success('Egg saved.');
    },
    onError: (e) => applyServerErrors(e, form.setError),
  });
  const remove = useMutation({
    mutationFn: () =>
      apiRequest(`/api/application/eggs/${egg.id}`, { method: 'DELETE', schema: okSchema }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin'] });
      await navigate({ to: '/admin/nests' });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const submit = form.handleSubmit(
    (v) => save.mutate(v),
    () => toast.error('Some fields need attention; check each tab.'),
  );

  return (
    <>
      <PageHeader
        title={egg.name}
        icon={EggIcon}
        description={`by ${egg.author} · used by ${egg.serverCount} server${egg.serverCount === 1 ? '' : 's'}`}
        actions={
          <>
            <Button variant="outline" asChild>
              <a href={`/api/application/eggs/${egg.id}/export`} download>
                <Download /> Export
              </a>
            </Button>
            <Confirm
              title={`Delete ${egg.name}?`}
              description="Eggs in use by servers can't be deleted."
              action="Delete"
              destructive
              onConfirm={() => remove.mutate()}
            >
              <Button variant="outline" className="text-destructive" disabled={egg.serverCount > 0}>
                <Trash2 /> Delete
              </Button>
            </Confirm>
            <Button
              onClick={() => void submit()}
              disabled={save.isPending || !form.formState.isDirty}
            >
              {save.isPending ? <Loader2 className="animate-spin" /> : <Save />} Save
            </Button>
          </>
        }
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
          <TabsTrigger value="install">Install script</TabsTrigger>
          <TabsTrigger value="variables">Variables ({egg.variables.length})</TabsTrigger>
          <TabsTrigger value="images">Images</TabsTrigger>
        </TabsList>
        <TabsContent value="configuration" className="mt-6">
          <ConfigurationTab egg={egg} form={form} />
        </TabsContent>
        <TabsContent value="install" className="mt-6">
          <InstallTab egg={egg} form={form} />
        </TabsContent>
        <TabsContent value="variables" className="mt-6">
          <VariablesTab egg={egg} />
        </TabsContent>
        <TabsContent value="images" className="mt-6">
          <ImagesTab egg={egg} />
        </TabsContent>
      </Tabs>
    </>
  );
}

type EggForm = ReturnType<typeof useForm<EggInput>>;

function ParentEggSelect({
  value,
  onChange,
  excludeId,
  label,
}: {
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  excludeId: number;
  label: string;
}) {
  const eggs = useQuery(allEggsQuery);
  return (
    <FormField label={label}>
      <Select
        value={value ? String(value) : 'none'}
        onValueChange={(v) => onChange(v === 'none' ? null : Number(v))}
      >
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">None</SelectItem>
          {eggs.data
            ?.filter((e) => e.id !== excludeId)
            .map((e) => (
              <SelectItem key={e.id} value={String(e.id)}>
                {e.name}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </FormField>
  );
}

function ProfilePinSelect({
  value,
  onChange,
  error,
}: {
  value: string | null | undefined;
  onChange: (v: string | null) => void;
  error?: string;
}) {
  const profiles = useQuery(gameProfilesQuery);
  return (
    <FormField
      label="Profile"
      error={error}
      description={
        <>
          Matching picks the profile from its rules. A pinned profile wins over matching while it is
          enabled.{' '}
          <Link to="/admin/game-profiles" className="underline">
            Manage profiles
          </Link>
        </>
      }
    >
      <Select value={value ?? 'auto'} onValueChange={(v) => onChange(v === 'auto' ? null : v)}>
        <SelectTrigger className="w-full sm:w-80">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="auto">Match automatically</SelectItem>
          {profiles.data?.map((p) => (
            <SelectItem key={p.slug} value={p.slug}>
              {p.name}
              {p.enabled ? '' : ' (disabled)'}
            </SelectItem>
          ))}
          {value && profiles.data && !profiles.data.some((p) => p.slug === value) && (
            <SelectItem value={value}>{value}</SelectItem>
          )}
        </SelectContent>
      </Select>
    </FormField>
  );
}

function ConfigurationTab({ egg, form }: { egg: EggDetail; form: EggForm }) {
  const nests = useQuery(nestsQuery);
  const e = form.formState.errors;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>General</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <FormField label="Name" error={e.name?.message}>
            <Input {...form.register('name')} />
          </FormField>
          <FormField label="Author" error={e.author?.message}>
            <Input {...form.register('author')} />
          </FormField>
          <Controller
            control={form.control}
            name="nestId"
            render={({ field }) => (
              <FormField label="Nest" error={e.nestId?.message}>
                <Select
                  value={String(field.value)}
                  onValueChange={(v) => field.onChange(Number(v))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {nests.data?.map((n) => (
                      <SelectItem key={n.id} value={String(n.id)}>
                        {n.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            )}
          />
          <FormField label="Description" error={e.description?.message}>
            <Textarea rows={4} {...form.register('description')} />
          </FormField>
          <Controller
            control={form.control}
            name="dockerImages"
            render={({ field }) => (
              <FormField
                label="Docker images"
                error={(e.dockerImages as { message?: string } | undefined)?.message}
                description="One per line: “Label | image”. The first is the default."
              >
                <Textarea
                  rows={5}
                  className="font-mono text-xs"
                  defaultValue={imagesToText(field.value)}
                  onChange={(ev) => field.onChange(textToImages(ev.target.value))}
                />
              </FormField>
            )}
          />
          <Controller
            control={form.control}
            name="features"
            render={({ field }) => (
              <FormField
                label="Features"
                description="Panel features this egg uses (e.g. eula, java_version), one per line."
              >
                <Textarea
                  rows={2}
                  className="font-mono text-xs"
                  defaultValue={listToText(field.value ?? [])}
                  onChange={(ev) => field.onChange(textToList(ev.target.value))}
                />
              </FormField>
            )}
          />
          <Controller
            control={form.control}
            name="fileDenylist"
            render={({ field }) => (
              <FormField
                label="File denylist"
                description="Files users may not edit, one pattern per line."
              >
                <Textarea
                  rows={2}
                  className="font-mono text-xs"
                  defaultValue={listToText(field.value ?? [])}
                  onChange={(ev) => field.onChange(textToList(ev.target.value))}
                />
              </FormField>
            )}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Game profile</CardTitle>
          <CardDescription>
            Decides how servers of this egg look and which tabs they get. Servers use{' '}
            <span className="text-foreground font-medium">{egg.profile.identity.name}</span> now.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Controller
            control={form.control}
            name="profileSlug"
            render={({ field }) => (
              <ProfilePinSelect
                value={field.value}
                onChange={field.onChange}
                error={e.profileSlug?.message}
              />
            )}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Process</CardTitle>
          <CardDescription>
            How hearthd starts the server and knows it is ready and how to stop it.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <FormField
            label="Startup command"
            error={e.startup?.message}
            description="Use {{VARIABLE}} placeholders."
          >
            <Textarea rows={3} className="font-mono text-xs" {...form.register('startup')} />
          </FormField>
          <FormField
            label="Stop command"
            error={e.configStop?.message}
            description="Sent to the console to stop gracefully. ^C sends SIGINT; empty uses docker stop."
          >
            <Input className="font-mono" {...form.register('configStop')} />
          </FormField>
          <Controller
            control={form.control}
            name="configFromId"
            render={({ field }) => (
              <ParentEggSelect
                label="Copy configuration from"
                value={field.value}
                onChange={field.onChange}
                excludeId={egg.id}
              />
            )}
          />
          {(
            [
              ['configStartup', 'Startup detection (JSON)', 'e.g. {"done": ")! For help, type "}'],
              [
                'configFiles',
                'Configuration file parsers (JSON)',
                'Applied before each start (used from milestone 7).',
              ],
              ['configLogs', 'Logs (JSON)', 'Kept for Pterodactyl compatibility.'],
            ] as const
          ).map(([name, label, hint]) => (
            <Controller
              key={name}
              control={form.control}
              name={name}
              render={({ field }) => (
                <FormField label={label} error={e[name]?.message} description={hint}>
                  <div>
                    <LazyCodeEditor
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      language="json"
                      height={name === 'configFiles' ? 200 : 100}
                      ariaLabel={label}
                    />
                  </div>
                </FormField>
              )}
            />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function InstallTab({ egg, form }: { egg: EggDetail; form: EggForm }) {
  const e = form.formState.errors;
  const copyFrom = form.watch('copyScriptFromId');
  return (
    <Card>
      <CardHeader>
        <CardTitle>Install script</CardTitle>
        <CardDescription>
          Runs once in a temporary container as root, with the server’s files at{' '}
          <code>/mnt/server</code> and the egg variables in its environment.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <FormField label="Install container image" error={e.scriptContainer?.message}>
            <Input className="font-mono text-xs" {...form.register('scriptContainer')} />
          </FormField>
          <FormField
            label="Entrypoint"
            error={e.scriptEntry?.message}
            description="bash, ash or sh"
          >
            <Input className="font-mono" {...form.register('scriptEntry')} />
          </FormField>
          <Controller
            control={form.control}
            name="copyScriptFromId"
            render={({ field }) => (
              <ParentEggSelect
                label="Use script from another egg"
                value={field.value}
                onChange={field.onChange}
                excludeId={egg.id}
              />
            )}
          />
        </div>
        {copyFrom ? (
          <p className="text-muted-foreground text-sm">
            This egg runs the selected egg’s install script, container and entrypoint.
          </p>
        ) : (
          <Controller
            control={form.control}
            name="scriptInstall"
            render={({ field }) => (
              <LazyCodeEditor
                value={field.value ?? ''}
                onChange={field.onChange}
                language="shell"
                height={520}
                ariaLabel="Install script"
              />
            )}
          />
        )}
      </CardContent>
    </Card>
  );
}

function VariablesTab({ egg }: { egg: EggDetail }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<EggVariable | 'new' | null>(null);
  const remove = useMutation({
    mutationFn: (id: number) =>
      apiRequest(`/api/application/eggs/${egg.id}/variables/${id}`, {
        method: 'DELETE',
        schema: eggDetailSchema,
      }),
    onSuccess: (d) => queryClient.setQueryData(eggKey(egg.id), d),
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setEditing('new')}>
          <Plus /> New variable
        </Button>
      </div>
      <div className="table-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Variable</TableHead>
              <TableHead>Default</TableHead>
              <TableHead>Rules</TableHead>
              <TableHead>Users</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {egg.variables.map((v) => (
              <TableRow key={v.id}>
                <TableCell>
                  <div className="font-medium">{v.name}</div>
                  <div className="text-muted-foreground font-mono text-xs">{v.envVariable}</div>
                </TableCell>
                <TableCell className="max-w-48 truncate font-mono text-xs">
                  {v.defaultValue || '—'}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {v.rules}
                  {v.unknownRules.length > 0 && (
                    <div className="text-warning mt-1 flex items-center gap-1 font-sans">
                      <AlertTriangle className="size-3" /> Not checked: {v.unknownRules.join(', ')}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-xs">
                  {v.userViewable ? (
                    v.userEditable ? (
                      'view & edit'
                    ) : (
                      'view only'
                    )
                  ) : (
                    <span className="text-muted-foreground">hidden</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Edit ${v.envVariable}`}
                    onClick={() => setEditing(v)}
                  >
                    <Pencil />
                  </Button>
                  <Confirm
                    title={`Delete ${v.envVariable}?`}
                    description="Values set on existing servers are deleted too."
                    action="Delete"
                    destructive
                    onConfirm={() => remove.mutate(v.id)}
                  >
                    <Button variant="ghost" size="icon" aria-label={`Delete ${v.envVariable}`}>
                      <Trash2 />
                    </Button>
                  </Confirm>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {editing && (
        <VariableDialog
          eggId={egg.id}
          variable={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

type VariableValues = z.input<typeof eggVariableInputSchema>;

function VariableDialog({
  eggId,
  variable,
  onClose,
}: {
  eggId: number;
  variable: EggVariable | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const form = useForm<VariableValues>({
    resolver: zodResolver(eggVariableInputSchema),
    defaultValues: variable
      ? { ...variable }
      : {
          name: '',
          description: '',
          envVariable: '',
          defaultValue: '',
          userViewable: true,
          userEditable: true,
          rules: 'nullable|string',
          sort: 0,
        },
  });
  const save = useMutation({
    mutationFn: (body: VariableValues) =>
      apiRequest(
        variable
          ? `/api/application/eggs/${eggId}/variables/${variable.id}`
          : `/api/application/eggs/${eggId}/variables`,
        {
          method: variable ? 'PATCH' : 'POST',
          body,
          schema: eggDetailSchema,
        },
      ),
    onSuccess: (d) => {
      queryClient.setQueryData(eggKey(eggId), d);
      onClose();
    },
    onError: (e) => applyServerErrors(e, form.setError),
  });
  const e = form.formState.errors;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{variable ? `Edit ${variable.envVariable}` : 'New variable'}</DialogTitle>
        </DialogHeader>
        <form
          id="variable-form"
          className="grid gap-4 sm:grid-cols-2"
          noValidate
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
        >
          <FormField label="Name" error={e.name?.message}>
            <Input {...form.register('name')} />
          </FormField>
          <FormField label="Environment variable" error={e.envVariable?.message}>
            <Input className="font-mono" {...form.register('envVariable')} />
          </FormField>
          <div className="sm:col-span-2">
            <FormField label="Description" error={e.description?.message}>
              <Textarea rows={2} {...form.register('description')} />
            </FormField>
          </div>
          <FormField label="Default value" error={e.defaultValue?.message}>
            <Input className="font-mono" {...form.register('defaultValue')} />
          </FormField>
          <FormField
            label="Rules"
            error={e.rules?.message}
            description="Laravel style, e.g. required|integer|between:1,100"
          >
            <Input className="font-mono" {...form.register('rules')} />
          </FormField>
          {(['userViewable', 'userEditable'] as const).map((name) => (
            <Controller
              key={name}
              control={form.control}
              name={name}
              render={({ field }) => (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={name}
                    checked={field.value}
                    onCheckedChange={(c) => field.onChange(c === true)}
                  />
                  <Label htmlFor={name}>
                    {name === 'userViewable' ? 'Users can see it' : 'Users can edit it'}
                  </Label>
                </div>
              )}
            />
          ))}
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="variable-form" disabled={save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImagesTab({ egg }: { egg: EggDetail }) {
  const queryClient = useQueryClient();
  const nodes = useQuery(nodesQuery);
  const [nodeId, setNodeId] = useState<string>('');
  const chosen = nodes.data?.find((n) => String(n.id) === nodeId) ?? nodes.data?.[0];
  const nativeArch = chosen?.health.system?.docker.arch ?? 'arm64';
  const inspect = useMutation({
    mutationFn: () =>
      apiRequest(`/api/application/eggs/${egg.id}/images/inspect`, {
        method: 'POST',
        body: { nodeId: chosen!.id },
        schema: eggDetailSchema,
      }),
    onSuccess: (d) => queryClient.setQueryData(eggKey(egg.id), d),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const rows: [string, string][] = [
    ...Object.entries(egg.dockerImages),
    ['Installer', egg.scriptContainer],
  ];
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Architectures</CardTitle>
          <CardDescription>
            Images without a native {nativeArch} build run under emulation, which is slower and can
            break some games. An image can also be multi-arch while the game binary inside it is
            x86-only; see the egg compatibility notes.
          </CardDescription>
        </div>
        <div className="flex gap-2">
          {(nodes.data?.length ?? 0) > 1 && (
            <Select value={nodeId || String(chosen?.id ?? '')} onValueChange={setNodeId}>
              <SelectTrigger className="w-40" aria-label="Node to check from">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {nodes.data?.map((n) => (
                  <SelectItem key={n.id} value={String(n.id)}>
                    {n.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button onClick={() => inspect.mutate()} disabled={!chosen || inspect.isPending}>
            {inspect.isPending ? <Loader2 className="animate-spin" /> : <Cpu />} Check images
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!chosen && (
          <p className="text-muted-foreground mb-4 text-sm">
            Add a node to check images through it.
          </p>
        )}
        <ul className="divide-y">
          {rows.map(([label, image]) => {
            const info = egg.imageArch[image];
            const arches = info?.platforms.map((p) => p.split('/')[1]) ?? [];
            return (
              <li key={label + image} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{label}</div>
                  <div className="text-muted-foreground truncate font-mono text-xs">{image}</div>
                </div>
                {!info ? (
                  <Badge variant="outline">Not checked</Badge>
                ) : info.error ? (
                  <Badge variant="destructive" title={info.error}>
                    Lookup failed
                  </Badge>
                ) : arches.includes(nativeArch) ? (
                  <Badge className="bg-success/15 text-success border-success/30">
                    Native {nativeArch}
                  </Badge>
                ) : (
                  <Badge className="bg-warning/15 text-warning border-warning/30">
                    Emulated ({arches.join(', ') || 'unknown'})
                  </Badge>
                )}
                {info && (
                  <span className="text-muted-foreground text-xs">{timeAgo(info.checkedAt)}</span>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
